export interface Post {
  title: string;
  slug: string;
  image: string;
  category: string;
  content: string;
}

export const allPosts: Post[] = [

{
  title: "O Sono e a Saúde Ocular: Como Dormir Bem Protege Sua Visão",
  slug: "sono-saude-ocular-como-dormir-bem-protege-sua-visao",
  image: "/blog/images/sono-saude-ocular.webp",
  category: "Cuidados com os Olhos",
  content: `
<p>Dormir bem não é apenas uma questão de descanso: é um pilar essencial para a saúde ocular. Noites mal dormidas podem causar <strong>olho seco</strong>, <strong>visão turva</strong>, <strong>tremores palpebrais</strong> e até agravar condições oculares pré-existentes. Entender essa relação ajuda a prevenir sintomas, melhorar o conforto visual e proteger a visão a longo prazo.</p>

<h3>Por que o sono é importante para a visão?</h3>
<p>Durante o sono, o organismo realiza processos essenciais de reparo celular, equilíbrio hormonal e renovação das estruturas oculares. Dormir pouco ou mal afeta diretamente a lubrificação dos olhos, a qualidade da visão e até o risco de algumas doenças.</p>

<h3>1) Dormir pouco favorece o olho seco</h3>
<p>Durante o sono, a superfície ocular se recupera e a produção de lágrimas se regula. Quando o descanso é insuficiente, pode ocorrer:</p>
<ul>
  <li>Evaporação aumentada;</li>
  <li>Redução da qualidade da lágrima;</li>
  <li>Sensação de areia nos olhos;</li>
  <li>Visão oscilante ao longo do dia.</li>
</ul>
<p>Pessoas que usam telas por longos períodos tendem a sentir esses efeitos com mais intensidade.</p>

<h3>2) Cansaço visual e visão turva</h3>
<p>O corpo usa o sono para relaxar os músculos responsáveis pela acomodação ocular. Quando eles não descansam adequadamente, podem surgir:</p>
<ul>
  <li>Visão embaçada;</li>
  <li>Dificuldade para focar;</li>
  <li>Dor ao redor dos olhos;</li>
  <li>Sensibilidade à luz.</li>
</ul>
<p>É comum associar isso a “problema no grau”, mas muitas vezes é apenas fadiga visual relacionada ao sono ruim.</p>

<h3>3) Blefaroespasmos e tremores nas pálpebras</h3>
<p>Pálpebras tremendo sem parar são um sintoma clássico de privação de sono. Isso pode acontecer por:</p>
<ul>
  <li>Fadiga muscular;</li>
  <li>Estresse;</li>
  <li>Baixa hidratação;</li>
  <li>Desregulação neuromuscular temporária.</li>
</ul>
<p>Em geral, são episódios benignos, mas podem ser bastante incômodos e durar dias.</p>

<h3>4) Sono ruim pode piorar doenças oculares</h3>
<p>Algumas condições podem se agravar quando o descanso é insuficiente.</p>

<h4>Glaucoma</h4>
<p>A pressão intraocular tende a variar durante o sono. Pessoas que dormem muito tarde, têm apneia ou privação crônica podem ter maior risco de progressão.</p>

<h4>Apneia do sono</h4>
<p>A apneia está associada a condições como:</p>
<ul>
  <li>Glaucoma;</li>
  <li>Neuropatia óptica;</li>
  <li>Edema de papila;</li>
  <li>Olho seco;</li>
  <li><em>Floppy eyelid syndrome</em> (pálpebra frouxa).</li>
</ul>

<h4>Doenças inflamatórias</h4>
<p>Quem tem olho seco grave, uveítes ou doenças autoimunes pode notar piora dos sintomas após noites mal dormidas.</p>

<h3>5) Falta de sono afeta a retina</h3>
<p>Estudos indicam que a privação crônica pode interferir no metabolismo da retina, impactando processos como adaptação à luz, contraste e sensibilidade visual. Não causa cegueira por si só, mas pode somar fatores em quem já tem doenças pré-existentes.</p>

<h3>6) Como dormir melhor e proteger os olhos</h3>
<p>Algumas medidas simples ajudam tanto a qualidade do sono quanto a saúde ocular:</p>
<ul>
  <li>Reduzir telas 1 a 2 horas antes de dormir;</li>
  <li>Manter o ambiente escuro e silencioso;</li>
  <li>Evitar cafeína à noite;</li>
  <li>Hidratar-se ao longo do dia;</li>
  <li>Usar lágrimas artificiais quando houver olho seco (com orientação profissional);</li>
  <li>Tratar apneia do sono quando presente;</li>
  <li>Fazer pausas visuais durante o dia (regra 20-20-20).</li>
</ul>
<p>Pequenas mudanças podem reduzir irritação, ardência e visão turva ao longo do dia.</p>

<h3>7) Quando procurar um oftalmologista?</h3>
<p>A avaliação é indicada quando, além de sono ruim, surgem:</p>
<ul>
  <li>Dor ocular persistente;</li>
  <li>Sensibilidade excessiva à luz;</li>
  <li>Visão embaçada frequente;</li>
  <li>Olho vermelho recorrente;</li>
  <li>Desconforto matinal intenso;</li>
  <li>Tremores palpebrais por mais de 2 semanas.</li>
</ul>
<p>Esses sintomas podem indicar olho seco, blefarite, glaucoma ou apneia do sono mal controlada.</p>

<h3>Conclusão: dormir bem é cuidar da visão</h3>
<p>O sono é um dos pilares da saúde ocular. Ele lubrifica, protege, recupera e estabiliza as funções dos olhos. Priorizar boas noites de descanso melhora o conforto visual, reduz inflamações e ajuda a manter a visão saudável por muitos anos.</p>

<!-- Meta-description sugerida:
Dormir mal pode causar olho seco, visão turva, tremores palpebrais e até piorar doenças oculares. Entenda como o sono afeta seus olhos e como melhorar. -->
`
},

{
  title: "Medicamentos que Podem Afetar Sua Visão: Entenda os Sinais e Quando Procurar Ajuda",
  slug: "medicamentos-afetam-visao-quando-procurar-ajuda",
  image: "/blog/images/medicacoes-visao.webp",
  category: "Cuidados com os Olhos",
  content: `
<p>Muitos medicamentos usados no dia a dia podem alcançar estruturas sensíveis dos olhos através da circulação sanguínea. Dependendo da substância e do tempo de uso, podem surgir efeitos como <strong>visão turva</strong>, <strong>olho seco</strong>, <strong>aumento da pressão ocular</strong> ou até alterações na retina. Conhecer esses riscos ajuda a identificar sinais precoces e manter o tratamento com segurança.</p>

<h3>1) Corticoides: risco de catarata e glaucoma</h3>
<p>Amplamente utilizados para alergias, doenças autoimunes, inflamações e problemas respiratórios, os corticoides exigem atenção especial. Em tratamentos prolongados, podem causar:</p>
<ul>
  <li><strong>Aumento da pressão ocular</strong> (glaucoma);</li>
  <li><strong>Catarata</strong> em longo prazo;</li>
  <li><strong>Atraso na cicatrização</strong> de estruturas oculares.</li>
</ul>

<h3>2) Antialérgicos, ansiolíticos e antidepressivos</h3>
<p>Alguns desses medicamentos têm efeito anticolinérgico, interferindo na lubrificação e na acomodação visual. Podem provocar:</p>
<ul>
  <li><strong>Olho seco</strong>;</li>
  <li><strong>Visão embaçada</strong>;</li>
  <li><strong>Risco de crise de glaucoma</strong> em pessoas predispostas.</li>
</ul>

<h3>3) Isotretinoína (Roacutan) e derivados</h3>
<p>Conhecida pelo uso no tratamento da acne, pode causar:</p>
<ul>
  <li><strong>Ressecamento ocular</strong>;</li>
  <li><strong>Intolerância a lentes de contato</strong>;</li>
  <li><strong>Pior adaptação à visão noturna</strong>.</li>
</ul>
<p>Esses efeitos geralmente são reversíveis após o fim do tratamento.</p>

<h3>4) Anticoagulantes e antiagregantes</h3>
<p>Incluem aspirina, clopidogrel e novas medicações da mesma classe. Em alguns pacientes, podem aumentar o risco de <strong>sangramentos oculares</strong>, especialmente em quem já possui doenças da retina.</p>

<h3>5) Medicamentos para diabetes, incluindo GLP-1</h3>
<p>Medicamentos como <strong>semaglutida</strong> e <strong>tirzepatida</strong> podem causar:</p>
<ul>
  <li><strong>Oscilações do grau</strong>;</li>
  <li><strong>Agravamento inicial</strong> da retinopatia diabética em casos específicos.</li>
</ul>

<h3>6) Hidroxicloroquina: atenção redobrada</h3>
<p>Usada no tratamento de lúpus e artrite reumatoide. Embora segura para a maioria dos pacientes, pode causar <strong>toxicidade retiniana</strong> quando utilizada por muitos anos.</p>
<p>O acompanhamento deve incluir exames como <strong>campimetria</strong> e <strong>OCT</strong>.</p>

<h3>7) Amiodarona</h3>
<p>Usada para arritmias cardíacas. Seus possíveis efeitos incluem:</p>
<ul>
  <li><strong>Depósitos na córnea</strong> (geralmente sem impacto visual);</li>
  <li><strong>Alterações no nervo óptico</strong> (raras).</li>
</ul>

<h3>8) Medicamentos para disfunção erétil</h3>
<p>Fármacos como sildenafila e tadalafila podem causar:</p>
<ul>
  <li><strong>Sensibilidade à luz</strong>;</li>
  <li><strong>Visão azulada</strong>;</li>
  <li>Alterações raras no nervo óptico em pessoas predispostas.</li>
</ul>

<h3>Quando procurar um oftalmologista com urgência?</h3>
<p>Busque avaliação imediata se notar, após iniciar um medicamento:</p>
<ul>
  <li><strong>Visão turva persistente</strong>;</li>
  <li><strong>Manchas, sombras ou flashes de luz</strong>;</li>
  <li><strong>Mudança súbita no campo visual</strong>;</li>
  <li><strong>Dor ocular</strong>;</li>
  <li><strong>Alteração na percepção de cores</strong>;</li>
  <li><strong>Vermelhidão intensa</strong> acompanhada de dor ou náusea.</li>
</ul>
<p><strong>Nunca interrompa uma medicação sem orientação médica.</strong></p>

<h3>Como prevenir problemas oculares causados por medicamentos?</h3>
<ul>
  <li>Informe ao oftalmologista todas as medicações em uso.</li>
  <li>Realize consultas periódicas quando utilizar remédios com potencial tóxico.</li>
  <li>Controle doenças como <strong>diabetes</strong> e <strong>hipertensão</strong>.</li>
  <li>Evite alterar doses por conta própria.</li>
</ul>

<h3>Conclusão</h3>
<p>A maioria dos medicamentos é segura quando usada corretamente, mas alguns podem impactar a visão — especialmente em tratamentos prolongados. Reconhecer os sinais cedo e manter acompanhamento oftalmológico regular é fundamental para preservar a saúde ocular.</p>

<!-- Meta-description sugerida:
Alguns medicamentos podem causar efeitos na visão, como olho seco, visão turva e até alterações na retina. Saiba quais remédios afetam os olhos e quando procurar um oftalmologista. -->
`
}
 ,

  {
    title: "Como um AVC Pode Afetar Sua Visão: Entenda os Sinais e as Consequências",
    slug: "avc-pode-afetar-sua-visao",
    image: "/blog/images/avc-visao.webp",
    category: "Doenças Oculares",
    content: `
<p>Um Acidente Vascular Cerebral (AVC), popularmente conhecido como “derrame”, é uma emergência médica que ocorre quando o fluxo de sangue para uma parte do cérebro é interrompido. Além dos conhecidos prejuízos motores e de fala, o AVC pode causar impactos profundos na visão. Como oftalmologista, explico neste artigo como o AVC afeta os olhos e o processamento visual, e por que o acompanhamento com um especialista é crucial para a reabilitação.</p>

<h3>1) Perda de Campo Visual: enxergar “metade” do mundo</h3>
<p>Um dos efeitos mais comuns do AVC na visão é a <strong>perda de campo visual</strong>, quando a região do cérebro responsável por processar a imagem é atingida.</p>
<ul>
  <li><strong>Hemianopsia homônima:</strong> o paciente deixa de enxergar o lado direito ou esquerdo do campo visual de ambos os olhos, como se metade do mundo estivesse “apagada”.</li>
  <li><strong>Impacto no dia a dia:</strong> esbarrar em objetos, dificuldade para ler e para se locomover.</li>
</ul>

<h3>2) Danos aos centros superiores de processamento visual</h3>
<p>Mesmo com os olhos saudáveis, o cérebro pode <em>não interpretar corretamente</em> o que vemos. O AVC pode afetar áreas responsáveis por funções complexas, como:</p>
<ul>
  <li><strong>Alexia:</strong> incapacidade de ler, mesmo reconhecendo letras e palavras.</li>
  <li><strong>Agrafia:</strong> dificuldade para escrever, ainda que a habilidade motora esteja preservada.</li>
  <li><strong>Negligência visual:</strong> ignorar um lado do espaço (ex.: comer apenas a comida de um lado do prato).</li>
</ul>

<h3>3) Alterações na acomodação e no movimento ocular</h3>
<p>O AVC pode atingir nervos e núcleos cerebrais que controlam os músculos dos olhos, causando:</p>
<ul>
  <li><strong>Visão dupla (diplopia):</strong> devido ao desalinhamento dos olhos.</li>
  <li><strong>Dificuldade de focar:</strong> problemas de acomodação — como se a “lente” do olho não ajustasse bem.</li>
  <li><strong>Nistagmo:</strong> movimentos involuntários dos olhos.</li>
</ul>

<h3>O que fazer? Diagnóstico e reabilitação</h3>
<ul>
  <li><strong>Avaliação urgente:</strong> ao primeiro sinal de AVC (dor de cabeça súbita, assimetria facial, fraqueza em um lado do corpo, perda visual), busque atendimento <strong>imediato</strong>.</li>
  <li><strong>Acompanhamento com neuro-oftalmologista:</strong> especialista que integra neurologia e oftalmologia.</li>
  <li><strong>Terapia visual:</strong> exercícios para estimular a plasticidade cerebral e compensar perdas.</li>
  <li><strong>Estratégias práticas:</strong> óculos com <em>prismas</em>, adaptar o ambiente e treinar a girar a cabeça/olhar para compensar o campo visual perdido.</li>
</ul>

<p>As sequelas visuais do AVC são frequentes e podem ser devastadoras, mas <strong>a reabilitação existe e traz esperança</strong>. Com diagnóstico precoce e uma equipe multidisciplinar (neurologista, oftalmologista, terapeuta ocupacional), muitos pacientes recuperam parte da função visual e readquirem independência.</p>

<h4>Referências consultadas</h4>
<ul>
  <li>American Stroke Association (2023). <em>Stroke and Vision Loss</em>.</li>
  <li><em>Journal of Neuro-Ophthalmology</em> (2022). <em>Rehabilitation in Visual Field Defects</em>.</li>
</ul>
`,
  },

  {
    title:
      "“Canetas Emagrecedoras” e a Visão: Entenda a Relação entre Diabetes, Perda de Peso e Saúde Ocular",
    slug: "canetas-emagrecedoras-visao-diabetes",
    image: "/blog/images/emagrecimento.webp",
    category: "Cuidados com os Olhos",
    content: `
<p>Você já ouviu falar das famosas “canetas emagrecedoras”? Esses medicamentos (<strong>Ozempic, Mounjaro, Wegovy, Saxenda e Victoza</strong>) têm sido amplamente utilizados no tratamento do diabetes e da obesidade, ajudando milhares de pessoas a controlarem o açúcar no sangue e a perderem peso. Mas será que eles afetam a saúde dos olhos? Como oftalmologista especialista em retina, explico neste artigo o que a ciência já sabe – e o que ainda está em estudo – sobre a relação entre esses remédios e doenças oculares.</p>

<h3>1) Efeitos na Retinopatia Diabética</h3>
<p>Alguns estudos iniciais sugeriram que as “canetas” poderiam, em certos casos, <strong>acelerar temporariamente</strong> a piora da retinopatia diabética. <em>Mas calma</em>: isso não significa que o remédio seja vilão.</p>
<p><strong>Por que isso acontece?</strong> Quando o controle do diabetes melhora <strong>muito rapidamente</strong> (com quedas bruscas da glicemia), a retina pode sofrer um “choque” temporário, precisando se readaptar.</p>
<p><strong>Boas notícias:</strong> evidências mais recentes mostram que, <strong>a longo prazo</strong>, o bom controle do diabetes traz <strong>mais benefícios do que riscos</strong> para a retina. O essencial é manter <strong>acompanhamento regular</strong> com seu oftalmologista.</p>

<h3>2) Podem causar danos ao nervo óptico?</h3>
<p>Há relatos raros de <strong>neuropatia óptica isquêmica</strong>, mas <strong>sem prova</strong> de causa direta pelos fármacos. Suspeita-se que efeitos como <strong>náuseas e desidratação</strong> possam reduzir o fluxo sanguíneo em pacientes predispostos.</p>
<p><strong>Recomendação:</strong> hidrate-se bem, faça um <strong>exame oftalmológico</strong> antes de iniciar o uso (há nervos mais predispostos) e procure seu médico se houver <strong>perda visual súbita</strong>.</p>

<h3>3) E quem tem Degeneração Macular (DMRI)?</h3>
<p>Não há evidências de piora ou melhora direta da DMRI por essas medicações. Como a DMRI se relaciona a idade e genética, <strong>controlar diabetes e peso</strong> ajuda indiretamente a saúde ocular.</p>

<h3>4) Outras condições oculares</h3>
<ul>
  <li><strong>Glaucoma e catarata:</strong> sem relação de causa e efeito comprovada.</li>
  <li><strong>Olho seco:</strong> alguns pacientes relatam melhora indireta, possivelmente por redução inflamatória com a perda de peso.</li>
</ul>

<h3>Dicas práticas</h3>
<ul>
  <li><strong>Não interrompa</strong> o tratamento sem conversar com o médico prescritor — os benefícios metabólicos e cardiovasculares são comprovados.</li>
  <li>Faça um <strong>check-up ocular</strong> antes de iniciar as “canetas”, especialmente se você tem diabetes de longa data.</li>
  <li>Mantenha <strong>retornos regulares</strong> ao oftalmologista nos primeiros anos de uso.</li>
</ul>

<h4>Referências consultadas</h4>
<ul>
  <li>Ensaios clínicos <strong>SUSTAIN-6</strong> e <strong>LEADER</strong> (New England Journal of Medicine).</li>
  <li>Diretrizes da <strong>Sociedade Brasileira de Diabetes (2023)</strong>.</li>
</ul>
`,
  },

  {
    title:
      "Alzheimer e Parkinson: O Papel do Oftalmologista no Diagnóstico Precoce e Manejo",
    slug: "alzheimer-parkinson-oftalmologista-diagnostico-manejo",
    image: "/blog/images/alzheimer-parkinson.webp",
    category: "Cuidados com os Olhos",
    content: `
<p>Alzheimer e Parkinson são doenças neurodegenerativas que afetam milhões de pessoas em todo o mundo. O que poucos sabem é que os olhos podem ser uma “janela para o cérebro”, revelando sinais precoces dessas condições. Como oftalmologista, explico neste artigo como alterações oculares ajudam no diagnóstico precoce e no acompanhamento dessas doenças, e por que consultar um especialista em visão é parte crucial do cuidado multidisciplinar.</p>

<h3>1) Alterações Oculares como Biomarcadores Precoces</h3>
<p>Muitas vezes, os olhos mostram sinais de doenças neurodegenerativas antes mesmo do surgimento de sintomas clássicos — a retina é uma extensão do sistema nervoso central.</p>
<p><strong>No Alzheimer:</strong></p>
<ul>
  <li><strong>Redução da espessura</strong> da camada de fibras nervosas da retina (medida por <em>OCT</em>).</li>
  <li><strong>Depósitos de beta-amiloide</strong> na retina, detectáveis por técnicas de imagem avançada.</li>
  <li><strong>Alterações nos vasos retinianos</strong> (estreitamento arterial e perda de vascularização).</li>
</ul>
<p><strong>No Parkinson:</strong></p>
<ul>
  <li><strong>Perda de células ganglionares</strong> (dopaminérgicas) da retina, associada a:
    <ul>
      <li>Defeitos no campo visual;</li>
      <li>Redução da percepção de cores (especialmente azul–amarelo);</li>
      <li>Queda da sensibilidade ao contraste.</li>
    </ul>
  </li>
</ul>

<h3>2) Parkinson: Piscar Menos e Superfície Ocular</h3>
<p>No Parkinson, a <strong>frequência do piscar</strong> diminui (rigidez/bradicinesia), levando a:</p>
<ul>
  <li><strong>Olho seco</strong> por evaporação acelerada da lágrima;</li>
  <li><strong>Visão flutuante</strong> e desconforto ocular.</li>
</ul>
<p><strong>Manejo:</strong> lubrificantes oculares, óculos com proteção lateral e, em casos de <em>blefarospasmo</em>, toxina botulínica.</p>

<h3>3) Outras Alterações Oculares Comuns</h3>
<p><strong>No Alzheimer:</strong></p>
<ul>
  <li>Lentificação da busca visual (movimentação ocular voluntária);</li>
  <li>Dificuldade de atenção visual;</li>
  <li><strong>Alexia</strong> (não consegue ler) mesmo com acuidade preservada.</li>
</ul>
<p><strong>No Parkinson:</strong></p>
<ul>
  <li><em>Blefarospasmo</em> (fechamento involuntário das pálpebras);</li>
  <li>Crises oculógiras (olhos desviam para cima/lados involuntariamente);</li>
  <li>Convergência deficiente (dificuldade para perto/leitura).</li>
</ul>

<h3>4) Como o Oftalmologista Pode Ajudar?</h3>
<p><strong>Diagnóstico precoce:</strong></p>
<ul>
  <li>OCT de retina e nervo óptico para detectar afinamento de camadas;</li>
  <li>Testes de sensibilidade ao contraste e visão de cores.</li>
</ul>
<p><strong>Manejo de sintomas oculares:</strong></p>
<ul>
  <li>Lubrificantes no olho seco do Parkinson;</li>
  <li>Lentes com filtros para melhorar contraste.</li>
</ul>
<p><strong>Encaminhamento multidisciplinar:</strong> integração com neurologia, geriatria e terapia ocupacional.</p>

<h3>5) O Futuro: Inteligência Artificial e Olhos</h3>
<p>Estudos já utilizam <strong>IA</strong> para analisar imagens de retina e estimar risco de Alzheimer ou Parkinson <strong>anos antes</strong> dos sintomas — um caminho promissor para prevenção e acompanhamento.</p>

<h3>Conclusão</h3>
<p>O oftalmologista é um aliado essencial no diagnóstico precoce e no manejo de Alzheimer e Parkinson. Alterações oculares podem ser os primeiros sinais; intervenções simples (como lubrificantes) já melhoram a qualidade de vida. <strong>Não subestime sintomas oculares em pacientes neurológicos</strong> — eles podem ser a chave para um cuidado mais integral.</p>

<h4>Referências consultadas</h4>
<ul>
  <li>London et al. (2013). <em>The Eye as a Window to the Brain in Neurodegenerative Diseases</em>.</li>
  <li><em>Journal of Parkinson’s Disease</em> (2021). <em>Retinal Changes in Parkinson’s Disease</em>.</li>
  <li><em>Alzheimer’s &amp; Dementia</em> (2022). <em>Retinal Biomarkers in Early Alzheimer’s</em>.</li>
</ul>
`,
  },
{
  title:
    "Uso Indiscriminado de Colírios: Os Perigos da Automedicação para a Saúde Ocular",
  slug: "uso-indiscriminado-colirios-automedicacao-saude-ocular",
  image: "/blog/images/colirios.webp",
  category: "Cuidados com os Olhos",
  content: `
<p>Vermelhidão, irritação ou coceira nos olhos são queixas comuns que levam muitas pessoas a buscar colírios em farmácias sem prescrição médica. No entanto, o que parece um alívio imediato pode esconder riscos graves. Como oftalmologista, alerto neste artigo sobre os perigos do uso indiscriminado de colírios, especialmente aqueles com corticoides e vasoconstritores, e por que a automedicação ocular pode custar sua visão.</p>

<h3>1) Colírios com Corticoides</h3>
<p>Os corticoides tópicos (como dexametasona ou prednisolona) são potentes anti-inflamatórios, mas seu uso sem supervisão médica é uma das principais causas de <strong>cegueira irreversível</strong> em pacientes jovens e saudáveis.</p>

<h4>Riscos principais:</h4>
<ul>
  <li><strong>Glaucoma corticogênico:</strong> aumento da pressão intraocular que danifica o nervo óptico silenciosamente, podendo causar perda visual permanente em semanas ou meses.</li>
  <li><strong>Catarata precoce:</strong> opacificação do cristalino, mesmo com uso de baixas doses.</li>
  <li><strong>Agravamento de infecções:</strong> o corticoide pode mascarar infecções virais (como herpes ocular) ou bacterianas, permitindo sua progressão rápida.</li>
</ul>

<h4>Casos clínicos comuns:</h4>
<ul>
  <li>Pacientes que usam “colírios milagrosos” para alergia ou conjuntivite sem saber que contêm corticoide.</li>
  <li>Uso prolongado de colírios comprados em farmácias sem receita ou recomendados por leigos.</li>
</ul>

<h3>2) Vasoconstrutores</h3>
<p>Colírios vasoconstritores (como nafazolina ou tetrizolina) são usados para clarear olhos vermelhos rapidamente, mas seu <strong>efeito rebote</strong> é perigoso.</p>

<h4>Riscos principais:</h4>
<ul>
  <li><strong>Vermelhidão de rebote:</strong> os vasos sanguíneos dilatam ainda mais após o efeito passar, criando um ciclo vicioso de uso.</li>
  <li><strong>Síndrome do olho seco severa:</strong> redução da produção de lágrimas e desestabilização do filme lacrimal.</li>
  <li><strong>Mascaramento de doenças graves:</strong> um olho vermelho pode ser sinal de uveíte, ceratite ou glaucoma agudo — condições que exigem tratamento urgente.</li>
</ul>

<h3>3) Outros Colírios Perigosos na Automedicação</h3>
<ul>
  <li><strong>Anestésicos tópicos:</strong> causam toxicidade corneana e úlceras perfuradas; o paciente perde a dor, mas continua com o dano.</li>
  <li><strong>Antibióticos sem prescrição:</strong> uso indevido pode gerar resistência bacteriana e superinfecções.</li>
</ul>

<h3>4) Por Que as Pessoas se Automedicam?</h3>
<ul>
  <li><strong>Acesso fácil:</strong> colírios são vendidos sem receita em muitas farmácias.</li>
  <li><strong>Publicidade enganosa:</strong> propagandas que prometem “clarear olhos em segundos”.</li>
  <li><strong>Falta de informação:</strong> muitos não sabem que colírios podem causar dependência ou efeitos sistêmicos.</li>
</ul>

<h3>5) Sinais de Alerta: Quando Procurar um Oftalmologista?</h3>
<ul>
  <li>Vermelhidão que persiste por mais de 48 horas.</li>
  <li>Dor ocular, visão borrada ou sensibilidade à luz.</li>
  <li>Secreção amarelada ou verde.</li>
  <li>Histórico de glaucoma na família.</li>
</ul>

<h3>6) O Tratamento Correto: Segurança em Primeiro Lugar</h3>
<ul>
  <li>Nunca use colírios sem prescrição médica.</li>
  <li>Desconfie de produtos “naturais” ou “milagrosos”.</li>
  <li>Use lágrimas artificiais sem conservantes se necessário.</li>
  <li>Procure um oftalmologista ao primeiro sinal de desconforto ocular persistente.</li>
</ul>

<h3>Conclusão</h3>
<p>Seus olhos são frágeis e vitais. O uso indiscriminado de colírios — especialmente com corticoides ou vasoconstritores — pode levar a consequências devastadoras, como glaucoma irreversível ou cegueira. Cuide da sua visão com responsabilidade e sempre com acompanhamento especializado.</p>

<h4>Referências consultadas</h4>
<ul>
  <li>American Academy of Ophthalmology (2023). <em>The Dangers of Non-Prescription Eye Drops</em>.</li>
  <li>Sociedade Brasileira de Oftalmologia (SBO). <em>Diretrizes de Uso Racional de Colírios</em>.</li>
</ul>
`,
},

  {
    title:
      "Avaliação da Retina Antes da Cirurgia de Catarata e Cirurgia Refrativa: Por Que É Essencial?",
    slug: "avaliacao-da-retina-antes-da-cirurgia",
    image: "/blog/images/avaliacao.webp",
    category: "Cirurgias Oftalmológicas",
    content: `Antes de realizar uma <strong>cirurgia de catarata</strong> ou <strong>cirurgia refrativa</strong>, um passo fundamental muitas vezes subestimado é a <strong>avaliação completa da retina</strong>...`
  },
  {
    title:
      "Sintomas que Podem Indicar Descolamento de Retina (Urgência Oftalmológica!)",
    slug: "sintomas-de-descolamento-de-retina",
    image: "/blog/images/descolamento-retina.webp",
    category: "Doenças Oculares",
    content: `O <strong>descolamento de retina (DR)</strong> é uma <strong>emergência médica</strong> que pode levar à perda permanente da visão se não tratado rapidamente...`
  },
  {
    title: "Prevenção da Degeneração Macular: Além dos Suplementos",
    slug: "prevencao-da-degeneracao-macular-dmri",
    image: "/blog/images/degeneracao-macular.webp",
    category: "Cuidados com os Olhos",
    content: `A <strong>Degeneração Macular Relacionada à Idade (DMRI)</strong> é uma das principais causas de perda de visão...`
  },
  {
    title:
      "DMRI Úmida: Como Tolerar Melhor as Injeções e Reduzir Sua Frequência?",
    slug: "dmri-umida-tratamento-confortavel",
    image: "/blog/images/dmri-umida.webp",
    category: "Tratamentos",
    content: `A <strong>Degeneração Macular</strong> na forma <strong>úmida</strong> requer tratamento com <strong>injeções intravítreas</strong>...`
  },
  {
    title:
      "Posicionamento Pós-Operatório em Cirurgias de Retina: Quando é Necessário Olhar para Baixo?",
    slug: "posicionamento-pos-operatorio-retina",
    image: "/blog/images/posicionamento.webp",
    category: "Cirurgias Oftalmológicas",
    content: `A orientação para manter um posicionamento específico após cirurgias de retina tem evoluído...`
  },
  {
    title:
      "Uveíte ou Esclerite Recorrente: O Que Fazer Quando as Crises se Repetem?",
    slug: "uveite-esclerite-recorrente",
    image: "/blog/images/crises-repetem.webp",
    category: "Doenças Oculares",
    content: `Quando a <strong>uveíte</strong> ou <strong>esclerite</strong> retornam com frequência, isso sinaliza a necessidade de uma abordagem mais aprofundada...`
  },
  {
    title:
      "Quais Exames São Necessários para Diagnosticar Toxoplasmose Ocular?",
    slug: "exames-diagnostico-toxoplasmose-ocular",
    image: "/blog/images/toxoplasmose-ocular.webp",
    category: "Tecnologias e Exames",
    content: `A toxoplasmose ocular é uma das causas mais comuns de inflamação intraocular...`
  },
  {
    title: "Medicações que Podem Causar Alterações no Fundo de Olho",
    slug: "medicacoes-alteracoes-fundo-de-olho",
    image: "/blog/images/medicacoes-olho.webp",
    category: "Cuidados com os Olhos",
    content: `Muitos não imaginam que medicamentos de uso comum podem causar alterações na retina...`
  },
  {
    title: "Quando um Bebê Prematuro Deve Fazer Exame de Retina?",
    slug: "exame-retina-bebe-prematuro-rop",
    image: "/blog/images/bebe-prematuro.webp",
    category: "Doenças Oculares",
    content: `A <strong>retinopatia da prematuridade (ROP)</strong> é uma das principais causas de cegueira infantil evitável...`
  },
  {
    title: "Edema Macular: Causas, Sintomas e Opções de Tratamento",
    slug: "edema-macular-causas-sintomas-tratamento",
    image: "/blog/images/edema-macular.webp",
    category: "Doenças Oculares",
    content: `O <strong>edema macular</strong> ocorre quando há acúmulo de líquido na mácula...`
  },
  {
    title: "Buraco Macular: O Que É, Sintomas e Tratamento",
    slug: "buraco-macular-o-que-e",
    image: "/blog/images/buraco-macular.webp",
    category: "Doenças Oculares",
    content: `O <strong>buraco macular</strong> é uma pequena ruptura na mácula...`
  },
  {
    title:
      "Membrana Epirretiniana: Quando a Cirurgia de Vitrectomia é Indicada?",
    slug: "membrana-epirretiniana-cirurgia-vitrectomia",
    image: "/blog/images/membrana-epirretiniana.webp",
    category: "Cirurgias Oftalmológicas",
    content: `A <strong>membrana epirretiniana (MER)</strong> é uma fina camada de tecido sobre a retina macular...`
  },
  {
    title:
      "Neuropatia Óptica vs. Neurite Óptica: Entendendo as Diferenças",
    slug: "neuropatia-optica-vs-neurite-optica",
    image: "/blog/images/neuropatia-neurite.webp",
    category: "Doenças Oculares",
    content: `As condições que afetam o nervo óptico podem causar perda visual significativa...`
  },
  {
    title:
      "Tenho Esclerose Múltipla: Como Diferenciar Neurite Óptica de Outros Problemas Oculares?",
    slug: "esclerose-multipla-diferenciar-neurite-optica",
    image: "/blog/images/esclerose-multipla.webp",
    category: "Dúvidas Frequentes",
    content: `Pacientes com <strong>esclerose múltipla (EM)</strong> frequentemente apresentam sintomas visuais...`
  },
  {
    title:
      "Papiledema: Quando o Inchaço do Nervo Óptico Sinaliza Problemas Graves",
    slug: "papiledema-inchaco-nervo-optico",
    image: "/blog/images/papiledema.webp",
    category: "Doenças Oculares",
    content: `O <strong>papiledema</strong> é um sinal clínico importante que pode indicar condições neurológicas sérias...`
  },
  {
    title:
      "Por Que Tenho Uveíte? Entendendo as Causas da Inflamação Ocular",
    slug: "por-que-tenho-uveite",
    image: "/blog/images/crises-repetem.webp",
    category: "Doenças Oculares",
    content: `A <strong>uveíte</strong> é uma condição inflamatória que afeta a camada média do olho...`
  },
  {
    title:
      "Tratamento com Imunossupressores para Uveítes Crônicas: Quando Vale a Pena e Quais os Riscos?",
    slug: "tratamento-imunossupressores-uveites-cronicas",
    image: "/blog/images/medicacoes-olho.webp",
    category: "Tratamentos",
    content: `Pacientes com <strong>uveítes crônicas</strong> ou recorrentes muitas vezes precisam de tratamentos mais potentes...`
  }
];

export const categories = [
  "Todas",
  "Doenças Oculares",
  "Cirurgias Oftalmológicas",
  "Cuidados com os Olhos",
  "Tecnologias e Exames",
  "Dúvidas Frequentes",
  "Tratamentos"
];
